/**
 * Admin reads for the performance-telemetry panel (see perf/perfTelemetry.ts
 * for the capture side). The global fetch interceptor attaches the JWT.
 */

import { API_BASE } from './apiBase';

export interface PerfFpsStats {
  avg: number; median: number; p5: number; p1: number; min: number;
  below50_pct: number; below30_pct: number;
}

export interface PerfSessionDoc {
  user: string;
  session_id: string;
  editor: string;
  started_at: number;
  received_at: number;
  flush_seq: number;
  device: {
    gpu?: string; gpu_vendor?: string; ua?: string;
    cores?: number | null; memory_gb?: number | null;
    screen?: [number, number]; device_pixel_ratio?: number;
    build?: string;
  };
  stats: {
    seconds?: number; playing_seconds?: number;
    fps?: PerfFpsStats & { max?: number };
    render?: {
      scale_avg?: number; scale_min?: number; dpr_last?: number;
      calls_avg?: number; calls_max?: number;
      tris_avg?: number; tris_max?: number;
      notes_drawn_max?: number; canvas?: [number, number];
    };
    timing?: {
      cpu_render_avg_ms?: number | null; cpu_render_max_ms?: number | null;
      gpu_med_ms?: number | null; gpu_p95_ms?: number | null; gpu_samples?: number;
      hitch_100ms?: number; hitch_250ms?: number; longest_frame_ms?: number;
    };
    memory?: {
      js_heap_max_mb?: number | null; js_heap_limit_mb?: number | null;
      textures_max?: number; geometries_max?: number;
    };
    hidden_count?: number;
    worst_seconds?: { fps: number; beat: number; calls: number; tris: number; notes: number; playing: boolean }[];
    mode_seconds?: Record<string, number>;
    chart_notes?: number;
  };
}

export interface PerfUserRow {
  email: string;
  sessions: number;
  seconds: number;
  last_seen: number;
  gpu: string;
  device_pixel_ratio?: number;
  screen?: [number, number];
  cores?: number | null;
  memory_gb?: number | null;
  /** Editor build they ran (e.g. "v0.1.0 · 6c6e891"); absent on old sessions. */
  build?: string | null;
  /** Seconds spent in each perf mode across the summarised sessions. */
  mode_seconds?: Record<string, number>;
  /** The mode they spent the most time in. */
  dominant_mode?: string | null;
  /** Timing block of the worst session (CPU vs GPU bound, hitches). */
  worst_timing?: PerfSessionDoc['stats']['timing'];
  editors: Record<string, number>;
  fps_avg: number | null;
  fps_p5: number | null;
  below50_pct: number | null;
  worst_session: {
    editor?: string; started_at?: number; fps?: PerfFpsStats;
    render?: PerfSessionDoc['stats']['render']; chart_notes?: number;
  } | null;
}

async function extractError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return body?.detail || `Request failed (${res.status})`;
  } catch {
    return `Request failed (${res.status})`;
  }
}

export async function perfSummary(): Promise<{ users: PerfUserRow[]; generated_at: number }> {
  const res = await fetch(`${API_BASE}/api/telemetry/perf/summary`);
  if (!res.ok) throw new Error(await extractError(res));
  return await res.json();
}

export async function perfUserSessions(email: string): Promise<{ email: string; sessions: PerfSessionDoc[] }> {
  const res = await fetch(`${API_BASE}/api/telemetry/perf/user/${encodeURIComponent(email)}`);
  if (!res.ok) throw new Error(await extractError(res));
  return await res.json();
}
